import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'
import SOCKET_NAMESPACES from 'socket-resources/namespaces/index'
import createSocketConnection from 'socket-resources/socket-connection/index'
import { WALLET_USER_WALLET_BALANCE } from 'socket-resources/event-constants/common-events'
import { getAuthTokenFromCookie } from 'helpers/cookie.helpers'
import { handleSessionExpired, isSessionInvalidMessage } from 'helpers/session.helpers'

/** Singleton while logged in so Holdem unmount does not drop the connection (lobby still receives balance). */
let walletSocketInstance = null

/**
 * `/wallet` namespace — connect when authenticated; subscribe to balance pushes via `subscribeUserWalletBalance`.
 */
const useWalletSocketNameSpace = () => {
  const { isLoggedIn } = useSelector((state) => state.auth || {})

  useEffect(() => {
    if (!isLoggedIn) {
      if (walletSocketInstance) {
        walletSocketInstance.removeAllListeners()
        walletSocketInstance.disconnect()
        walletSocketInstance = null
      }
      return undefined
    }

    const token = getAuthTokenFromCookie()
    if (!token) {
      return undefined
    }

    const existing = walletSocketInstance
    if (existing && (existing.connected || existing.connecting)) {
      return undefined
    }

    if (existing && !existing.connected) {
      existing.removeAllListeners?.()
      existing.disconnect()
      walletSocketInstance = null
    }

    walletSocketInstance = createSocketConnection(SOCKET_NAMESPACES.WALLET, token)
    if (!walletSocketInstance) {
      return undefined
    }

    const onConnectError = (err) => {
      console.error('Wallet socket connect error:', err?.message || err)
      if (isSessionInvalidMessage(err?.message)) {
        handleSessionExpired()
      }
    }
    walletSocketInstance.on('connect_error', onConnectError)

    return () => {
      walletSocketInstance?.off('connect_error', onConnectError)
    }
  }, [isLoggedIn])

  const subscribeUserWalletBalance = useCallback((handler) => {
    if (!handler || !walletSocketInstance) {
      return () => {}
    }
    walletSocketInstance.on(WALLET_USER_WALLET_BALANCE, handler)
    return () => {
      walletSocketInstance?.off(WALLET_USER_WALLET_BALANCE, handler)
    }
  }, [])

  return { subscribeUserWalletBalance }
}

export default useWalletSocketNameSpace
